import React, { useState, useEffect } from "react";
import { FiRefreshCw, FiRotateCw } from "react-icons/fi";
import styles from "../styles/home.module.css";

const FlashcardSession = ({ 
  flashcardData, 
  onComplete, 
  onExit,
  onOpenInModal
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [userProgress, setUserProgress] = useState({});
  const [sessionCompleted, setSessionCompleted] = useState(false);
  
  // Initialize user progress
  useEffect(() => {
    if (flashcardData && flashcardData.cards) {
      const initialProgress = {};
      flashcardData.cards.forEach(card => {
        initialProgress[card.id] = {
          viewed: false,
          flipped: false
        };
      });
      setUserProgress(initialProgress);
    }
  }, [flashcardData]);

  const handleNext = () => {
    // Mark current card as viewed
    setUserProgress(prev => ({
      ...prev,
      [flashcardData.cards[currentIndex].id]: {
        ...prev[flashcardData.cards[currentIndex].id],
        viewed: true
      }
    }));
    
    if (currentIndex < flashcardData.cards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowAnswer(false);
    } else {
      // Session completed
      setSessionCompleted(true);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setShowAnswer(false);
    }
  };

  const handleFlip = () => {
    setShowAnswer(!showAnswer);
    
    // Mark card as flipped
    setUserProgress(prev => ({
      ...prev,
      [flashcardData.cards[currentIndex].id]: {
        ...prev[flashcardData.cards[currentIndex].id],
        flipped: true
      }
    }));
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setShowAnswer(false);
    setSessionCompleted(false);
    
    // Reset progress
    const resetProgress = {};
    flashcardData.cards.forEach(card => {
      resetProgress[card.id] = {
        viewed: false,
        flipped: false
      };
    });
    setUserProgress(resetProgress);
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete(userProgress);
    }
  };

  // Calculate session statistics
  const calculateStats = () => {
    const total = flashcardData.cards.length;
    const viewed = Object.values(userProgress).filter(p => p.viewed).length;
    const flipped = Object.values(userProgress).filter(p => p.flipped).length;
    const completion = total > 0 ? Math.round((viewed / total) * 100) : 0;
    
    return { total, viewed, flipped, completion };
  };

  if (!flashcardData || !flashcardData.cards || flashcardData.cards.length === 0) {
    return <div className={styles.errorMessage}>No flashcard data available</div>;
  }

  if (sessionCompleted) {
    const stats = calculateStats();
    
    return (
      <div className={styles.quizSummary}>
        <h3 className={styles.summaryHeader}>Flashcard Session Complete!</h3>
        
        <div className={styles.summaryStats}>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{stats.total}</div>
            <div className={styles.statLabel}>Total Cards</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{stats.viewed}</div>
            <div className={styles.statLabel}>Viewed</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{stats.flipped}</div>
            <div className={styles.statLabel}>Flipped</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{stats.completion}%</div>
            <div className={styles.statLabel}>Completion</div>
          </div>
        </div>
        
        <div className={styles.quizActionButtons}>
          <button 
            onClick={handleRestart} 
            className={`${styles.quizButton} ${styles.restartButton}`}
          >
            <FiRefreshCw /> Restart Session
          </button>
          <button 
            onClick={handleComplete} 
            className={`${styles.quizButton} ${styles.completeButton}`}
          >
            Return to Chat
          </button>
        </div>
      </div>
    );
  }

  const currentCard = flashcardData.cards[currentIndex];

  return (
    <div className={styles.flashcardSessionContainer}>
      <div className={styles.quizScrollable}>
        <div className={styles.flashcardHeader}>
          <h3>{flashcardData.title}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            {flashcardData.description && <p style={{ margin: 0 }}>{flashcardData.description}</p>}
            {onOpenInModal && (
              <button className={styles.linkButton} onClick={() => onOpenInModal('flashcards')}>
                Open in modal
              </button>
            )}
          </div>
        </div>
        
        <div className={styles.flashcardProgress}>
          <div className={styles.progressTrack}>
            <div 
              className={styles.progressFill} 
              style={{ width: `${((currentIndex + 1) / flashcardData.cards.length) * 100}%` }}
            />
          </div>
          <span className={styles.progressText}>Card {currentIndex + 1} of {flashcardData.cards.length}</span>
        </div>

        {/* Simple reveal panel (no flip) */}
        <div className={styles.flashcard}>
          <div className={styles.flashcardContent}>
            {!showAnswer ? (
              <>
                <h3 className={styles.flashcardQuestion}>{currentCard.question}</h3>
                <button onClick={handleFlip} className={styles.linkButton}>Reveal answer</button>
              </>
            ) : (
              <>
                <h3 className={styles.flashcardAnswer}>{currentCard.answer}</h3>
                {currentCard.explanation && (
                  <p className={styles.flashcardExplanation}>{currentCard.explanation}</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      
      <div className={styles.flashcardNavigation}>
        <button 
          onClick={handlePrev} 
          disabled={currentIndex === 0}
          className={`${styles.navButton} ${currentIndex === 0 ? styles.disabled : ''}`}
        >
          Previous
        </button>
        {!showAnswer ? (
          <button onClick={handleFlip} className={styles.flipButton}>
            <FiRotateCw /> Reveal
          </button>
        ) : (
          <button onClick={handleFlip} className={styles.flipButton}>
            Hide
          </button>
        )}
        <button 
          onClick={handleNext}
          className={styles.navButton}
        >
          {currentIndex === flashcardData.cards.length - 1 ? 'Finish' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default FlashcardSession;
